import { createSlice } from '@reduxjs/toolkit';
import { createAppThunk } from '../createAppThunk';
import type { Employee, Organization, PreventionAssignment } from '@/types/Api';
import type { NormalizedError } from '@/types/NormalizedError';
import { fetchOrganization, updateEmployeeNote, updatePreventionDeadline } from '@/lib/apiClient';

interface OrganizationState {
  data: Organization | null;
  loading: boolean;
  error: NormalizedError | null;

  // Simplified tracking of updates for deadlines
  deadlineUpdates: {
    [preventionId: string]: {
      loading: boolean;
      error: NormalizedError | null;
    };
  };
  // Tracking updates for employee notes
  noteUpdates: {
    [employeeId: string]: {
      loading: boolean;
      error: NormalizedError | null | 'common.error';
    };
  };
  // Tracking completion operations
  completionOperations: {
    [preventionId: string]: {
      loading: boolean;
      error: NormalizedError | null;
    };
  };
}

const initialState: OrganizationState = {
  data: null,
  loading: false,
  error: null,
  deadlineUpdates: {},
  noteUpdates: {},
  completionOperations: {},
};

export const getOrganization = createAppThunk<Organization, { id: string; token: string }>(
  'organizations/getOrganization',
  async ({ id, token }, thunkAPI) => {
    const { data, error } = await fetchOrganization(id, token);

    if (error) {
      return thunkAPI.rejectWithValue(error);
    }
    return data as Organization;
  },
);

export const updatePreventionDeadlineThunk = createAppThunk<
  PreventionAssignment,
  { preventionId: string; orgId: string; newDate: Date; token: string }
>(
  'organizations/updatePreventionDeadline',
  async ({ preventionId, orgId, newDate, token }, thunkAPI) => {
    const { data, error } = await updatePreventionDeadline(preventionId, orgId, newDate, token);
    if (error) return thunkAPI.rejectWithValue(error);
    if (!data) throw new Error('No data returned');
    return data;
  },
);

export const updateEmployeeNoteThunk = createAppThunk<
  Employee,
  { employeeId: string; orgId: string; note: string; token: string }
>('organizations/updateEmployeeNote', async ({ employeeId, orgId, note, token }, thunkAPI) => {
  const { data, error } = await updateEmployeeNote(employeeId, orgId, note, token);
  if (error) return thunkAPI.rejectWithValue(error);
  if (!data) throw new Error('No data returned');
  return data;
});

export const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearDeadlineUpdateError: (state, action) => {
      const preventionId = action.payload;
      if (state.deadlineUpdates[preventionId]) {
        state.deadlineUpdates[preventionId].error = null;
      }
    },
    clearNoteUpdateError: (state, action) => {
      const employeeId = action.payload;
      if (state.noteUpdates[employeeId]) {
        state.noteUpdates[employeeId].error = null;
      }
    },
    clearCompletionOperationError: (state, action) => {
      const preventionId = action.payload;
      if (state.completionOperations[preventionId]) {
        state.completionOperations[preventionId].error = null;
      }
    },
    // Update completions for a prevention assignment
    updatePreventionCompletions: (state, action) => {
      const { preventionId, completions } = action.payload;

      if (!state.data?.employees) return;

      // Find the employee with this prevention assignment
      const employee = state.data.employees.find((emp) =>
        emp.preventionAssignments?.some((pa) => pa.id === preventionId),
      );
      if (!employee) return;

      // Update the completions in the prevention assignment
      const employeeIndex = state.data.employees.findIndex((emp) => emp.id === employee.id);
      if (employeeIndex === -1) return;

      const assignmentIndex = state.data.employees[employeeIndex].preventionAssignments?.findIndex(
        (pa) => pa.id === preventionId,
      );
      if (assignmentIndex === -1 || assignmentIndex === undefined) return;

      state.data.employees[employeeIndex].preventionAssignments[assignmentIndex].completions =
        completions;
    },
    // Add a single completion to a prevention assignment
    addPreventionCompletion: (state, action) => {
      const { preventionId, completion } = action.payload;

      if (!state.data?.employees) return;

      // Find the employee with this prevention assignment
      const employee = state.data.employees.find((emp) =>
        emp.preventionAssignments?.some((pa) => pa.id === preventionId),
      );
      if (!employee) return;

      // Add the completion to the prevention assignment
      const employeeIndex = state.data.employees.findIndex((emp) => emp.id === employee.id);
      if (employeeIndex === -1) return;

      const assignmentIndex = state.data.employees[employeeIndex].preventionAssignments?.findIndex(
        (pa) => pa.id === preventionId,
      );
      if (assignmentIndex === -1 || assignmentIndex === undefined) return;

      state.data.employees[employeeIndex].preventionAssignments[assignmentIndex].completions.push(
        completion,
      );
    },
    // Update an entire prevention assignment
    updatePreventionAssignment: (state, action) => {
      const { preventionAssignment } = action.payload;

      if (!state.data?.employees) return;

      // Find the employee with this prevention assignment
      const employee = state.data.employees.find((emp) =>
        emp.preventionAssignments?.some((pa) => pa.id === preventionAssignment.id),
      );
      if (!employee) return;

      // Update the entire prevention assignment
      const employeeIndex = state.data.employees.findIndex((emp) => emp.id === employee.id);
      if (employeeIndex === -1) return;

      const assignmentIndex = state.data.employees[employeeIndex].preventionAssignments?.findIndex(
        (pa) => pa.id === preventionAssignment.id,
      );
      if (assignmentIndex === -1 || assignmentIndex === undefined) return;

      state.data.employees[employeeIndex].preventionAssignments[assignmentIndex] =
        preventionAssignment;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Organization cases
      .addCase(getOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrganization.fulfilled, (state, action) => {
        state.loading = false;

        state.data = {
          ...action.payload,
        };
      })
      .addCase(getOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })

      // Update Prevention Deadline cases
      .addCase(updatePreventionDeadlineThunk.pending, (state, action) => {
        const preventionId = action.meta.arg.preventionId;
        state.deadlineUpdates[preventionId] = {
          loading: true,
          error: null,
        };
      })
      .addCase(updatePreventionDeadlineThunk.fulfilled, (state, action) => {
        const preventionAssignment = action.payload;
        const preventionId = preventionAssignment.id;

        state.deadlineUpdates[preventionId] = {
          loading: false,
          error: null,
        };

        if (!state.data?.employees) return;

        // Find the employee with this prevention assignment
        const employee = state.data.employees.find((emp) =>
          emp.preventionAssignments?.some((pa) => pa.id === preventionId),
        );
        if (!employee) return;

        // Update the prevention assignment in the employee's assignments
        const employeeIndex = state.data.employees.findIndex((emp) => emp.id === employee.id);
        if (employeeIndex === -1) return;

        const assignmentIndex = state.data.employees[
          employeeIndex
        ].preventionAssignments?.findIndex((pa) => pa.id === preventionId);
        if (assignmentIndex === -1 || assignmentIndex === undefined) return;

        state.data.employees[employeeIndex].preventionAssignments[assignmentIndex] =
          preventionAssignment;
      })
      .addCase(updatePreventionDeadlineThunk.rejected, (state, action) => {
        const error = action.payload;
        const preventionId = action.meta.arg.preventionId;

        state.deadlineUpdates[preventionId] = {
          loading: false,
          error: error ?? null,
        };
      })

      // Update Employee Note cases
      .addCase(updateEmployeeNoteThunk.pending, (state, action) => {
        const employeeId = action.meta.arg.employeeId;
        state.noteUpdates[employeeId] = {
          loading: true,
          error: null,
        };
      })
      .addCase(updateEmployeeNoteThunk.fulfilled, (state, action) => {
        const updatedEmployee = action.payload;
        const employeeId = updatedEmployee.id;

        state.noteUpdates[employeeId] = {
          loading: false,
          error: null,
        };

        if (!state.data?.employees) return;

        const employeeIndex = state.data.employees.findIndex((emp) => emp.id === employeeId);
        if (employeeIndex === -1) return;

        // Only update the note field instead of replacing the entire employee object
        state.data.employees[employeeIndex].note = updatedEmployee.note;
      })
      .addCase(updateEmployeeNoteThunk.rejected, (state, action) => {
        const error = action.payload;
        const employeeId = action.meta.arg.employeeId;

        state.noteUpdates[employeeId] = {
          loading: false,
          error: error || 'common.error',
        };
      });
  },
});

export const {
  clearDeadlineUpdateError,
  clearNoteUpdateError,
  clearCompletionOperationError,
  updatePreventionCompletions,
  addPreventionCompletion,
  updatePreventionAssignment,
} = organizationSlice.actions;
export default organizationSlice.reducer;
